import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { enabledMenuItems } from '../../../utils/remotes';
import {
  AccountTree,
  Paid,
  PointOfSale,
  Tune,
  PostAdd,
  AccountBalance,
  AccountBalanceWallet,
  Hub,
  Assessment,
  DataObject,
  DocumentScanner,
  Storage,
  BarChart,
  ArchiveRounded,
  Help,
  SupportAgentRounded,
  // ViewKanban, — пункт «Канбан» выведен из эксплуатации
  Workspaces,
} from '@mui/icons-material';
import { KeyboardDoubleArrowLeft, KeyboardDoubleArrowRight } from '@mui/icons-material';
import { useAppStore } from '../../../stores/appStore';

interface SidebarProps {
  drawerWidth: number;
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ComponentType;
  path: string;
}

const SUPPORT_BOT_URL = 'https://t.me/OsnovaSupport_bot';

const USER_GUIDE_URL =
  'https://docs.google.com/document/d/1Ibm_GZR05rOSCQ0q6witQNS7piFjMYFkOq9K3bjM7Js/edit?tab=t.ajbmhc1eo0ox';

const USER_GUIDE_TOOLTIP =
  'На странице документации воспользуйтесь кнопкой «Ask Gemini» в правом верхнем углу — она поможет быстро найти ответ на любой вопрос по содержимому документа с помощью ИИ';

const baseMenuItems = [
  { id: 'reports-new', label: 'sidebar.reportsNew', icon: Assessment, path: '/reports' },
  { id: 'superset', label: 'sidebar.superset', icon: BarChart, path: '/superset-embed' },
  { id: 'org-chart', label: 'sidebar.orgChart', icon: AccountTree, path: '/org-chart' },
  { id: 'doc-constructor', label: 'sidebar.docConstructor', icon: PostAdd, path: '/doc-constructor' },
  { id: 'tax', label: 'sidebar.tax', icon: Paid, path: '/tax' },
  { id: 'rules', label: 'sidebar.rules', icon: Tune, path: '/rules' },
  { id: 'payroll', label: 'sidebar.payroll', icon: PointOfSale, path: '/payroll' },
  { id: 'int-invoices', label: 'sidebar.intInvoices', icon: AccountBalance, path: '/int-invoices' },
  { id: 'bank-accounts', label: 'sidebar.bankAccounts', icon: AccountBalanceWallet, path: '/bank-accounts' },
  // Канбан выведен из эксплуатации: доска живёт в разделе «АРМ» (/arm).
  // { id: 'kanban', label: 'sidebar.kanban', icon: ViewKanban, path: '/kanban' },
  { id: 'arm', label: 'sidebar.arm', icon: Workspaces, path: '/arm' },
  { id: 'data-sources', label: 'sidebar.dataSources', icon: Storage, path: '/data-sources' },
  { id: 'aggregator-service', label: 'sidebar.aggregatorService', icon: DataObject, path: '/aggregator-service' },
  { id: 'integrations', label: 'sidebar.integrations', icon: Hub, path: '/integrations' },
  { id: 'files-recognition', label: 'sidebar.filesRecognition', icon: DocumentScanner, path: '/files-recognition' },
  { id: 'email-file-extractor', label: 'sidebar.emailFileExtractor', icon: ArchiveRounded, path: '/email-file-extractor' },
];

const menuItems = enabledMenuItems(baseMenuItems, import.meta.env as Record<string, string | undefined>);

const Sidebar: React.FC<SidebarProps> = ({ drawerWidth }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { sidebarOpen, setSidebarOpen, toggleSidebar } = useAppStore();
  const location = useLocation();
  const navigate = useNavigate();

  const handleDrawerClose = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const handleNavigation = (item: MenuItem) => {
    navigate(item.path);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const miniWidth = 64; // равен высоте хедера на десктопе

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <List disablePadding>
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            const isActive = location.pathname.startsWith(item.path);
            const menuButton = (
              <ListItemButton
                selected={isActive}
                onClick={() => handleNavigation(item)}
                sx={{
                  pt: index === 0 ? '6px' : undefined,
                  justifyContent: !isMobile && !sidebarOpen ? 'center' : 'flex-start',
                  px: !isMobile && !sidebarOpen ? 1 : 2,
                transition: 'background-color 180ms ease-in-out, color 180ms ease-in-out',
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.light,
                    color: theme.palette.primary.contrastText,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main,
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive ? theme.palette.primary.contrastText : 'inherit',
                    minWidth: !isMobile && !sidebarOpen ? 0 : 40,
                    mr: !isMobile && !sidebarOpen ? 0 : 2,
                    justifyContent: 'center',
                  transition: 'color 180ms ease-in-out',
                  }}
                >
                  <Icon />
                </ListItemIcon>
                {!isMobile && !sidebarOpen ? null : (
                <ListItemText 
                  primary={t(item.label)} 
                  sx={{ transition: 'color 180ms ease-in-out' }} 
                />
                )}
              </ListItemButton>
            );

            return (
              <ListItem key={item.id} disablePadding>
                {!isMobile && !sidebarOpen ? (
                  <Tooltip 
                    title={t(item.label)} 
                    placement="right"
                    arrow
                  >
                    {menuButton}
                  </Tooltip>
                ) : (
                  menuButton
                )}
              </ListItem>
            );
          })}
        </List>
      </Box>
      {/* Бот техподдержки */}
      <Box>
        <Tooltip
          title={!isMobile && !sidebarOpen ? t('sidebar.supportBot') : ''}
          placement="right"
          arrow
        >
          <ListItemButton
            onClick={() => window.open(SUPPORT_BOT_URL, '_blank', 'noopener,noreferrer')}
            sx={{
              justifyContent: !isMobile && !sidebarOpen ? 'center' : 'flex-start',
              px: !isMobile && !sidebarOpen ? 1 : 2,
              transition: 'background-color 180ms ease-in-out, color 180ms ease-in-out',
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: !isMobile && !sidebarOpen ? 0 : 40,
                mr: !isMobile && !sidebarOpen ? 0 : 2,
                justifyContent: 'center',
                transition: 'color 180ms ease-in-out',
              }}
            >
              <SupportAgentRounded />
            </ListItemIcon>
            {!isMobile && !sidebarOpen ? null : (
              <ListItemText primary={t('sidebar.supportBot')} sx={{ transition: 'color 180ms ease-in-out' }} />
            )}
          </ListItemButton>
        </Tooltip>
      </Box>
      {/* Руководство пользователя — фиксированный пункт над кнопкой сворачивания */}
      <Box>
        <Tooltip
          title={
            !isMobile && !sidebarOpen ? (
              <Box>
                <Typography variant="subtitle2">{t('sidebar.userGuide')}</Typography>
                <Typography variant="body2">{USER_GUIDE_TOOLTIP}</Typography>
              </Box>
            ) : (
              USER_GUIDE_TOOLTIP
            )
          }
          placement="right"
          arrow
        >
          <ListItemButton
            onClick={() => window.open(USER_GUIDE_URL, '_blank', 'noopener,noreferrer')}
            sx={{
              justifyContent: !isMobile && !sidebarOpen ? 'center' : 'flex-start',
              px: !isMobile && !sidebarOpen ? 1 : 2,
              transition: 'background-color 180ms ease-in-out, color 180ms ease-in-out',
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: !isMobile && !sidebarOpen ? 0 : 40,
                mr: !isMobile && !sidebarOpen ? 0 : 2,
                justifyContent: 'center',
                transition: 'color 180ms ease-in-out',
              }}
            >
              <Help />
            </ListItemIcon>
            {!isMobile && !sidebarOpen ? null : (
              <ListItemText primary={t('sidebar.userGuide')} sx={{ transition: 'color 180ms ease-in-out' }} />
            )}
          </ListItemButton>
        </Tooltip>
      </Box>
      {/* Нижняя кнопка управления разворачиванием/сворачиванием (только десктоп) */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          p: 1,
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover': { backgroundColor: '#42A5F5', color: '#fff' },
        }}
        role="button"
        aria-label={sidebarOpen ? 'collapse sidebar' : 'expand sidebar'}
        onClick={() => setSidebarOpen(!sidebarOpen)}
     >
        <Box
          component="div"
          style={{ background: 'transparent', border: 0, padding: 8, lineHeight: 0 }}
        >
          {sidebarOpen ? <KeyboardDoubleArrowLeft /> : <KeyboardDoubleArrowRight />}
        </Box>
      </Box>
    </Box>
  );

  const computedWidth = isMobile ? 0 : (sidebarOpen ? drawerWidth : miniWidth);

  return (
    <Box
      component="nav"
      sx={{ width: { sm: computedWidth }, flexShrink: { sm: 0 }, transition: 'width 180ms ease-in-out' }}
      aria-label="mailbox folders"
    >
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? sidebarOpen : true}
        onClose={handleDrawerClose}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: isMobile ? drawerWidth : (sidebarOpen ? drawerWidth : miniWidth),
            transition: 'width 180ms ease-in-out',
            top: { xs: '56px', sm: '64px' },
            height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' },
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: !isMobile && !sidebarOpen ? theme.palette.primary.main : undefined,
            color: !isMobile && !sidebarOpen ? theme.palette.primary.contrastText : undefined,
            marginTop: 0,
            paddingTop: 0,
            '& .MuiListItemIcon-root': {
              color: !isMobile && !sidebarOpen ? theme.palette.primary.contrastText : undefined,
            },
          },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
